import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#ed6c0215'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#ed6c02'};
`;

export const useAllLeaveApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/leaves/all?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        setData(res.data?.data?.data || []);
        setPagination({
          totalRowCount: res.data?.data?.totalRowCount || 0,
          pageSize: res.data?.data?.pageSize || 10,
          pageIndex: (res.data?.data?.pageIndex || 1) - 1,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const reload = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const _delete = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/v1/leaves/${id}`)));
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const statusLabel = useCallback((value) => {
    if (!value) return '-';
    return t(`leave.statusValues.${value}`, { defaultValue: value });
  }, [t]);

  const signatureLabel = useCallback((value) => {
    if (!value) return '-';
    return t(`leave.${value}`, { defaultValue: value });
  }, [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('leave.employee'),
      options: {
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.userProfilePicture} />
        ), filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'leaveVariationName', label: t('leave.type'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    dateFilterColumn('leaveStart', t('leave.startDate'), lang),
    dateFilterColumn('leaveEnd', t('leave.endDate'), lang),
    {
      name: 'cost', label: t('leave.cost'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const unit = row?.isHourly ? t('leave.hours') : t('leave.days');
          return v != null ? `${v} ${unit}` : '-';
        },
      },
    },
    {
      name: 'status', label: t('leave.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['waiting', 'approved', 'rejected', 'canceled'],
          defaultOp: 'eq',
          renderValue: (v) => statusLabel(v),
        },
        customFilterListOptions: { render: (v) => statusLabel(v) },
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabel(v)}</StatusBadge>,
      },
    },
    {
      name: 'signatureStatus', label: t('leave.signatureStatus'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['signed', 'unsigned'],
          defaultOp: 'eq',
          renderValue: (v) => signatureLabel(v),
        },
        customFilterListOptions: { render: (v) => signatureLabel(v) },
        customBodyRender: (v) => (
          <StatusBadge $status={v === 'signed' ? 'approved' : 'rejected'}>
            {signatureLabel(v)}
          </StatusBadge>
        ),
      },
    },
  ], [t, lang, data, statusLabel, signatureLabel]);

  return { fetching, data, pagination, columns, reload, _delete };
};
